/**
 * Bundled FT-IR spectra from instrument CSV (wavenumber cm⁻¹, % transmittance).
 */

import rawSampleA from "./spectra/Sample A Res 2.CSV?raw";
import rawSampleB from "./spectra/Sample B Res 2.CSV?raw";
import rawSampleC from "./spectra/Sample C Res 2.CSV?raw";
import rawSampleD from "./spectra/Sample D Res 2.CSV?raw";
import rawSampleE from "./spectra/Sample E Res 2.CSV?raw";

const TRANS_EPS = 1e-6;

/**
 * @param {number} percentT
 * @returns {number}
 */
export function percentTransmittanceToAbsorbance(percentT) {
  const t = Math.max(percentT / 100, TRANS_EPS);
  return Math.max(0, -Math.log10(t));
}

/**
 * @param {string} csvText
 * @returns {{ wavenumbers: number[], absorbances: number[] }}
 */
export function parseFtirCsv(csvText) {
  const wavenumbers = [];
  const absorbances = [];
  for (const line of csvText.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const parts = trimmed.split(",");
    if (parts.length < 2) continue;
    const wn = Number(parts[0]);
    const t = Number(parts[1]);
    if (!Number.isFinite(wn) || !Number.isFinite(t)) continue;
    wavenumbers.push(wn);
    absorbances.push(percentTransmittanceToAbsorbance(t));
  }
  return { wavenumbers, absorbances };
}

/** @type {readonly string[]} */
export const FTIR_SAMPLE_IDS = /** @type {const} */ ([
  "sample-a",
  "sample-b",
  "sample-c",
  "sample-d",
  "sample-e"
]);

const RAW_BY_ID = {
  "sample-a": rawSampleA,
  "sample-b": rawSampleB,
  "sample-c": rawSampleC,
  "sample-d": rawSampleD,
  "sample-e": rawSampleE
};

const LABEL_BY_ID = {
  "sample-a": "Sample A",
  "sample-b": "Sample B",
  "sample-c": "Sample C",
  "sample-d": "Sample D",
  "sample-e": "Sample E"
};

/**
 * Full catalog for experiment state (includes arrays for COLLECT_SPECTRUM).
 * @type {Record<string, { label: string, wavenumbers: number[], absorbances: number[] }>}
 */
export const FTIR_SAMPLE_CATALOG = Object.fromEntries(
  FTIR_SAMPLE_IDS.map((id) => {
    const { wavenumbers, absorbances } = parseFtirCsv(RAW_BY_ID[id]);
    return [id, { label: LABEL_BY_ID[id], wavenumbers, absorbances }];
  })
);

/** Stride used only for chart rendering (full data stays in measurements). */
export const FTIR_CHART_POINT_STRIDE = 8;

/**
 * @param {number[]} wavenumbers
 * @param {number[]} absorbances
 * @param {number} stride
 */
export function decimateSpectrumPair(wavenumbers, absorbances, stride) {
  if (stride <= 1) {
    return { wavenumbers: [...wavenumbers], absorbances: [...absorbances] };
  }
  const wn = [];
  const ab = [];
  const n = Math.min(wavenumbers.length, absorbances.length);
  for (let i = 0; i < n; i += stride) {
    wn.push(wavenumbers[i]);
    ab.push(absorbances[i]);
  }
  if (n > 0 && (n - 1) % stride !== 0) {
    wn.push(wavenumbers[n - 1]);
    ab.push(absorbances[n - 1]);
  }
  return { wavenumbers: wn, absorbances: ab };
}

/**
 * Payload for attempt persistence (labels only — no spectral arrays).
 * @returns {Record<string, { label: string }>}
 */
export function buildPersistedFtirSolutionsSnapshot() {
  return Object.fromEntries(
    FTIR_SAMPLE_IDS.map((id) => [
      id,
      { label: FTIR_SAMPLE_CATALOG[id].label }
    ])
  );
}

/**
 * True when persisted solutions look like the current lab sample keys.
 * @param {Record<string, unknown> | null | undefined} solutions
 */
export function isCurrentFtirSolutionShape(solutions) {
  if (!solutions || typeof solutions !== "object") return false;
  return FTIR_SAMPLE_IDS.every((id) =>
    Object.prototype.hasOwnProperty.call(solutions, id)
  );
}

/**
 * Full sample map for LOAD_SAMPLES. Always uses bundled spectra so persisted
 * attempts (labels-only or legacy polymer keys) still collect correctly.
 * @returns {Record<string, { label: string, wavenumbers: number[], absorbances: number[] }>}
 */
export function getFtirSamplesForLabState() {
  return Object.fromEntries(
    FTIR_SAMPLE_IDS.map((id) => {
      const e = FTIR_SAMPLE_CATALOG[id];
      return [
        id,
        {
          label: e.label,
          wavenumbers: [...e.wavenumbers],
          absorbances: [...e.absorbances]
        }
      ];
    })
  );
}
